import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Divider,
  Button,
} from '@mui/joy';
import {
  MapPin,
  CurrencyDollar as DollarSign,
  Calendar,
  User,
  PencilSimple as Edit2,
  Trash as Trash2,
  Eye,
  CheckCircle,
  ArrowCounterClockwise as UncompleteIcon,
} from '@phosphor-icons/react';
import type { Job } from '../types';

interface JobCardProps {
  job: Job;
  onEdit?: (job: Job) => void;
  onDelete?: (jobId: number) => void;
  onUncomplete?: (jobId: number) => void;
  backgroundColor?: string;
}

export const JobCard: React.FC<JobCardProps> = ({
  job,
  onEdit,
  onDelete,
  onUncomplete,
  backgroundColor,
}) => {
  const navigate = useNavigate();
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: job.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleViewDetails = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent drag events
    navigate(`/operations/jobs/${job.id}`);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      variant="outlined"
      sx={{
        cursor: 'move',
        opacity: isDragging ? 0.5 : 1,
        transition: 'all 0.2s ease',
        backgroundColor: backgroundColor || 'background.surface',
        flexShrink: 0,
        '&:hover': {
          boxShadow: 'md',
        },
        '&:hover .card-actions': {
          opacity: 1,
        },
      }}
    >
      {job.is_completed && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 2,
            py: 0.75,
            bgcolor: 'success.solidBg',
            borderBottom: '1px solid',
            borderColor: 'success.solidBg',
          }}
        >
          <CheckCircle size={12} style={{ color: 'white' }} />
          <Typography level="body-xs" sx={{ color: 'common.white', fontWeight: 600 }}>
            Completed
          </Typography>
        </Box>
      )}
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography level="body-xs" textColor="text.secondary" sx={{ mb: 0.5 }}>
              {job.job_number}
            </Typography>
            <Typography level="title-sm" sx={{ lineHeight: 1.2 }}>
              {job.name || job.address_1}
            </Typography>
          </Box>
          <Box
            className="card-actions"
            sx={{
              display: 'flex',
              gap: 0.5,
              opacity: job.is_completed && onUncomplete ? 1 : 0,
              transition: 'opacity 0.2s ease',
            }}
          >
            {onEdit && (
              <IconButton
                size="sm"
                variant="plain"
                color="neutral"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(job);
                }}
              >
                <Edit2 size={12} />
              </IconButton>
            )}
            {onDelete && (
              <IconButton
                size="sm"
                variant="plain"
                color="danger"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(job.id);
                }}
              >
                <Trash2 size={12} />
              </IconButton>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <MapPin size={12} />
            <Typography level="body-xs" textColor="text.secondary">
              {job.name ? `${job.address_1}, ` : ''}
              {job.city}, {job.state} {job.zip_code}
            </Typography>
          </Box>

          {job.value && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <DollarSign size={12} />
              <Typography level="body-xs" textColor="text.secondary">
                {formatCurrency(job.value)}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Calendar size={12} />
            <Typography level="body-xs" textColor="text.secondary">
              Created {formatDate(job.created_at)}
            </Typography>
          </Box>

          {job.assigned_to && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <User size={12} />
              <Typography level="body-xs" textColor="text.secondary">
                {typeof job.assigned_to === 'object' 
                  ? `${job.assigned_to.first_name} ${job.assigned_to.last_name}`
                  : `User #${job.assigned_to}`
                }
              </Typography>
            </Box>
          )}

          {job.associated_contact && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <User size={12} />
              <Typography level="body-xs" textColor="text.secondary">
                {job.associated_contact.first_name} {job.associated_contact.last_name}
              </Typography>
            </Box>
          )}
        </Box>

        {job.date_status_update && (
          <>
            <Divider sx={{ my: 1 }} />
            <Typography level="body-xs" textColor="text.tertiary">
              Last updated: {formatDate(job.date_status_update)}
            </Typography>
          </>
        )}

        <Divider sx={{ my: 1 }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="sm"
            variant="outlined"
            color="primary"
            startDecorator={<Eye size={14} />}
            onClick={handleViewDetails}
            sx={{ flex: 1 }}
          >
            View Details
          </Button>
          {job.is_completed && onUncomplete && (
            <Button
              size="sm"
              variant="soft"
              color="success"
              startDecorator={<UncompleteIcon size={14} />}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onUncomplete(job.id);
              }}
              sx={{ flex: 1 }}
            >
              Reopen Job
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
